import { useContext, useMemo } from "react";
import { CodeBlock, Stack } from "@altimateai/lego";
import styles from "./styles.module.scss";
import { HeaderSection } from "./TableDetails";
import { CloseIcon } from "../assets/icons";
import "prismjs/themes/prism-tomorrow.css";
import { useReactFlow } from "reactflow";
import { LineageContext, useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import { setModalArgs } from "../context/LineageSlice";
import ViewsTypeBadge from "./ViewsTypeBadge";
import {
  ExportFinalLineageArgs,
  ExportFinalTableLineageArgs,
  OpNodeArgs,
  ViewsCodeModalArgs,
} from "../types";
import { SQL_ICONS } from "./CustomNodes";
import { ExportFinalLineageModal, ExportFinalTableLineageModal } from "./ExportFinalLineageModal";
import { Dialog, DialogContent } from "@altimateai/lego";

const ViewsCodeModal = ({ viewsCodeArgs }: { viewsCodeArgs: ViewsCodeModalArgs }) => {
  const {
    state: { theme },
  } = useContext(LineageContext);
  const lineageDispatch = useLineageDispatch();

  const flow = useReactFlow();
  const table = useMemo(() => {
    if (!viewsCodeArgs) return "";
    return flow.getNode(viewsCodeArgs.table)?.data?.label;
  }, [flow, viewsCodeArgs]);

  const viewsCodesFlat = useMemo(() => {
    const arr = Object.values(viewsCodeArgs?.viewsCode || [])
      .flat()
      .filter(([, type]) => type === "Transformation")
      .map(([code]) => code);
    const result: string[] = [];
    for (const item of arr) {
      if (result.includes(item)) continue;
      result.push(item);
    }
    return result;
  }, [viewsCodeArgs?.viewsCode]);

  return (
    <>
      <div className={styles.close_button} onClick={() => lineageDispatch(setModalArgs(null))}>
        <CloseIcon />
      </div>
      <Stack direction="column" gap="sm">
        {table && <HeaderSection nodeType={viewsCodeArgs.nodeType} table={table} />}
        <Stack direction="column" gap="xs">
          <div className="text-dark-grey fs-xs">Column</div>
          <div className={styles.model_views_type}>{viewsCodeArgs.column}</div>
        </Stack>
        <Stack direction="column" gap="xs">
          <div className="text-dark-grey fs-xs">Type</div>
          <div className={styles.model_views_type}>
            <ViewsTypeBadge viewsType={viewsCodeArgs.viewsType} />
            {viewsCodeArgs.viewsType}
          </div>
        </Stack>
        {viewsCodesFlat.length > 0 && (
          <Stack direction="column" gap="xs">
            <div className="text-dark-grey fs-xs">List of transformations</div>
            {viewsCodesFlat.map(code => (
              <CodeBlock
                key={code}
                code={code}
                language="sql"
                editorTheme="tomorrow"
                theme={theme}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </>
  );
};

function OpNodeModal({ opNodeArgs }: { opNodeArgs: OpNodeArgs }) {
  const {
    state: { theme },
  } = useContext(LineageContext);
  return (
    <Stack direction="column" gap="sm">
      <div className={styles.table_details_header}>
        {SQL_ICONS[opNodeArgs.op_type]}
        <Stack direction="row" align="center">
          <div className="fw-semibold fs-5 lines-2">{opNodeArgs.op_type}</div>
        </Stack>
      </div>
      <Stack direction="column" gap="xs">
        <div className="text-dark-grey fs-xs">Code</div>
        <CodeBlock code={opNodeArgs.op_code} language="sql" editorTheme="tomorrow" theme={theme} />
      </Stack>
    </Stack>
  );
}

export const LineageModal = () => {
  const {
    state: { modalArgs },
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();
  if (!modalArgs) return null;
  return (
    <Dialog
      open={modalArgs.type !== "none"}
      onOpenChange={() => lineageDispatch(setModalArgs({ type: "none" }))}
    >
      <DialogContent className="al-tw-scope al-z-[1000]">
        {modalArgs.type === "views_code" && (
          <ViewsCodeModal viewsCodeArgs={modalArgs.args as ViewsCodeModalArgs} />
        )}
        {modalArgs.type === "op_node" && <OpNodeModal opNodeArgs={modalArgs.args as OpNodeArgs} />}
        {modalArgs.type === "export_final_lineage" && (
          <ExportFinalLineageModal modalArgs={modalArgs.args as ExportFinalLineageArgs} />
        )}
        {modalArgs.type === "export_final_table_lineage" && (
          <ExportFinalTableLineageModal modalArgs={modalArgs.args as ExportFinalTableLineageArgs} />
        )}
      </DialogContent>
    </Dialog>
  );
};
